/**
 * Feature Flags — Day 1 hardening kill-switches
 *
 * Server + client safe (no `server-only` import). Reads from
 * `process.env.NEXT_PUBLIC_*` directly so it works in:
 *   - Next.js middleware (`proxy.ts`)
 *   - Server components (`app/**\/page.tsx`)
 *   - Client components (`"use client"` files)
 *   - Edge runtime (middleware is Edge)
 *
 * **DEFAULT POLICY — dining defaults ON (kill-switch model).**
 *
 * Why ON-by-default for dining, despite ADR 0003 reading "off in prod":
 * Belvedere's dining system is feature-complete (159/159 CEO requirements,
 * 625+ tests) and is the surface staff use every meal service. Defaulting
 * OFF in production would break operational staff at launch. The flag's job
 * is to be a kill-switch the CEO can flip without a redeploy if a regression
 * shows up during the resident-data onboarding window — not to gate
 * day-one rollout of a feature that's already been live to staff for weeks.
 *
 * To DISABLE dining in production, set `NEXT_PUBLIC_ENABLE_DINING=false`
 * on the Vercel Production env scope and trigger a redeploy (or use
 * `vercel env pull` + Edge config if instant flip is needed later).
 *
 * Open question recorded in `docs/swarm/BRIDGE.md` "Questions for Admiral":
 * confirm or invert this default.
 *
 * --- Parsing rules ---
 *
 * String env values are parsed loosely:
 *   - "false" / "0" / "off" / "no" → false (case-insensitive)
 *   - "" / undefined              → use default
 *   - anything else               → true
 *
 * This matches how engineers tend to write env flags ("0", "off", "no")
 * without having to remember a single canonical truthy string.
 */

/**
 * Parse an env var into a boolean. Returns `defaultValue` for unset/empty.
 *
 * Exported so other flags can reuse it as they get added.
 */
export function parseBoolEnv(value: string | undefined, defaultValue: boolean): boolean {
  if (value === undefined || value === "") return defaultValue
  const v = value.trim().toLowerCase()
  if (v === "false" || v === "0" || v === "off" || v === "no") return false
  return true
}

/**
 * Routes gated by the dining kill-switch. `isDiningRoute` matches exact paths
 * or sub-paths (path + "/"), so order is irrelevant for correctness — alphabetical
 * here for readability.
 */
export const DINING_ROUTES = [
  "/food-runner",
  "/host",
  "/kitchen",
  "/server-v2",
  "/suite-service",
  "/team-lead",
] as const

/**
 * Is the given pathname a dining route that should be gated by the flag?
 * Used by `proxy.ts` middleware and by nav filters in client components.
 */
export function isDiningRoute(pathname: string): boolean {
  return DINING_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(route + "/")
  )
}

/**
 * Resolved feature flag values. Read at call time, NOT at module load —
 * so tests can mutate `process.env` before invoking.
 */
export function getFeatureFlags() {
  return {
    /**
     * Dining service available — host/server/kitchen/food-runner/team-lead/
     * suite-service routes. Default ON. Flip to "false" on Vercel to disable.
     */
    diningEnabled: parseBoolEnv(process.env.NEXT_PUBLIC_ENABLE_DINING, true),
    /**
     * Offline-first Phase 1 — op queue, entity cache, shell SW registration.
     * Default ON so dinner-service queue behavior stays available. Flip to
     * "false" on Vercel to kill queueing + shell SW immediately (redeploy).
     * @see docs/OFFLINE_FIRST_DESIGN.md
     */
    offlineFirstEnabled: parseBoolEnv(
      process.env.NEXT_PUBLIC_ENABLE_OFFLINE_FIRST,
      true,
    ),
  } as const
}

/**
 * Shorthand — true iff dining routes should be reachable.
 */
export function isDiningEnabled(): boolean {
  return getFeatureFlags().diningEnabled
}

/**
 * Shorthand — true iff offline capture/replay + shell SW are enabled.
 */
export function isOfflineFirstEnabled(): boolean {
  return getFeatureFlags().offlineFirstEnabled
}

/**
 * Query param key set on the redirect target so a client toast can announce
 * the kill switch. Picked up by `<DiningKillSwitchToast>` mounted in
 * `app/layout.tsx`.
 */
export const DINING_DISABLED_QUERY_KEY = "dining_off"
